/**
 * Game Logger Utility
 * Collects game data and sends it to the backend for neural network training
 */

// Game data collection
let currentGameData = null;
let gameStartTime = null;
let moveStartTimes = {};

/**
 * Initialize game logging
 */
export const initializeGameLogging = (config) => {
  const {
    boardSize,
    patternId,
    gameMode,
    player1Type,
    player2Type,
    player1Key,
    player2Key,
    player1TimeBudget,
    player2TimeBudget,
    gameSource = 'regular',
    tag = null
  } = config;

  console.log('🎮 Initializing game logging with config:', config);

  currentGameData = {
    game_uuid: generateUUID(),
    board_size: boardSize,
    pattern_id: patternId,
    game_mode: gameMode,
    game_source: gameSource,
    tag,
    player1_type: player1Type,
    player2_type: player2Type,
    player1_key: player1Key,
    player2_key: player2Key,
    player1_time_budget_ms: player1TimeBudget,
    player2_time_budget_ms: player2TimeBudget,
    moves: [],
    board_states: [],
    stats: {},
    // Note: IP address is captured server-side for security
  };

  gameStartTime = Date.now();
  moveStartTimes = {};
  
  console.log('🎮 Game logging initialized:', currentGameData.game_uuid);
  console.log('🎮 Current game data:', currentGameData);
};

/**
 * Log a move
 */
export const logMove = (moveData) => {
  if (!currentGameData) {
    console.warn('🎮 No active game logging session');
    return;
  }

  const {
    player,
    row,
    col,
    playerKey,
    boardState,
    availableMoves,
    threatMatrixX,
    threatMatrixO,
    evaluationScore
  } = moveData;

  const moveNumber = currentGameData.moves.length + 1;
  const moveStartTime = moveStartTimes[moveNumber - 1] || gameStartTime;
  const timeTaken = Date.now() - moveStartTime;

  // Log the move
  currentGameData.moves.push({
    move_number: moveNumber,
    player,
    row,
    col,
    player_key: playerKey,
    time_taken_ms: timeTaken
  });

  // Log the board state
  currentGameData.board_states.push({
    move_number: moveNumber,
    board_state: boardState,
    available_moves: availableMoves,
    threat_matrix_x: threatMatrixX,
    threat_matrix_o: threatMatrixO,
    evaluation_score: evaluationScore
  });

  console.log(`🎮 Move ${moveNumber} logged: ${player} at (${row}, ${col})`);
};

/**
 * Start timing a move
 */
export const startMoveTimer = (moveNumber) => {
  moveStartTimes[moveNumber] = Date.now();
};

/**
 * Complete game logging
 */
export const completeGameLogging = async (winner, totalMoves) => {
  console.log('🎮 Completing game logging:', { winner, totalMoves });
  console.log('🎮 Current game data exists:', !!currentGameData);
  
  if (!currentGameData) {
    console.warn('🎮 No active game logging session');
    return;
  }

  const gameDuration = Date.now() - gameStartTime;
  
  // Add final game data
  currentGameData.winner = winner;
  currentGameData.total_moves = totalMoves;
  currentGameData.game_duration_ms = gameDuration;

  // Calculate game statistics
  currentGameData.stats = calculateGameStats(currentGameData);

  try {
    console.log('🎮 Sending game data to backend:', currentGameData);
    
    // Send to backend
    const result = await sendGameData(currentGameData);
    
    console.log('🎮 Backend response:', result);
    
    if (result.success) {
      console.log('🎮 Game logged successfully:', result.game_id);
    } else {
      console.error('🎮 Game logging failed:', result.error);
    }
    
    return result;
    
  } catch (error) {
    console.error('🎮 Game logging error:', error);
    return { success: false, error: error.message };
  } finally {
    // Reset for next game
    currentGameData = null;
    gameStartTime = null;
    moveStartTimes = {};
  }
};

/**
 * Optional: request precise browser location and attach to current game
 */
export const requestAndAttachLocation = async () => {
  if (!currentGameData) return { ok: false, reason: 'no_game' };
  if (!('geolocation' in navigator)) return { ok: false, reason: 'unsupported' };
  try {
    const pos = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 8000
      });
    });
    const { latitude, longitude, accuracy } = pos.coords;
    const timestamp = pos.timestamp; // ms since epoch
    currentGameData.gps_lat = latitude;
    currentGameData.gps_lon = longitude;
    currentGameData.gps_accuracy_m = accuracy;
    currentGameData.gps_timestamp = timestamp;
    return { ok: true, latitude, longitude, accuracy };
  } catch (e) {
    return { ok: false, reason: e?.message || 'denied' };
  }
};

/**
 * Calculate game statistics
 */
const calculateGameStats = (gameData) => {
  const player1Moves = gameData.moves.filter(m => m.player === 'X');
  const player2Moves = gameData.moves.filter(m => m.player === 'O');
  
  const player1Times = player1Moves.map(m => m.time_taken_ms).filter(t => t !== null);
  const player2Times = player2Moves.map(m => m.time_taken_ms).filter(t => t !== null);
  
  return {
    player1_avg_move_time_ms: player1Times.length > 0 ? 
      player1Times.reduce((a, b) => a + b, 0) / player1Times.length : null,
    player2_avg_move_time_ms: player2Times.length > 0 ? 
      player2Times.reduce((a, b) => a + b, 0) / player2Times.length : null,
    player1_longest_move_ms: player1Times.length > 0 ? Math.max(...player1Times) : null,
    player2_longest_move_ms: player2Times.length > 0 ? Math.max(...player2Times) : null,
    total_threats_created: calculateThreats(gameData),
    total_blocks_made: calculateBlocks(gameData),
    center_control_score: calculateCenterControl(gameData),
    pattern_completion_percentage: calculatePatternCompletion(gameData)
  };
};

/**
 * Calculate threats created
 */
const calculateThreats = (gameData) => {
  // Count moves that created winning threats
  let threats = 0;
  for (let i = 0; i < gameData.board_states.length; i++) {
    const state = gameData.board_states[i];
    if (state.threat_matrix_x || state.threat_matrix_o) {
      // Count high-threat positions
      const maxThreat = Math.max(
        ...(state.threat_matrix_x || []).flat(),
        ...(state.threat_matrix_o || []).flat()
      );
      if (maxThreat > 0.7) threats++;
    }
  }
  return threats;
};

/**
 * Calculate blocks made
 */
const calculateBlocks = (gameData) => {
  // Count defensive moves
  let blocks = 0;
  for (let i = 1; i < gameData.moves.length; i++) {
    const move = gameData.moves[i];
    const prevState = gameData.board_states[i - 1];
    
    // Check if this move blocked an opponent threat
    if (prevState.threat_matrix_x && move.player === 'O') {
      const threatLevel = prevState.threat_matrix_x[move.row]?.[move.col] || 0;
      if (threatLevel > 0.5) blocks++;
    } else if (prevState.threat_matrix_o && move.player === 'X') {
      const threatLevel = prevState.threat_matrix_o[move.row]?.[move.col] || 0;
      if (threatLevel > 0.5) blocks++;
    }
  }
  return blocks;
};

/**
 * Calculate center control
 */
const calculateCenterControl = (gameData) => {
  const boardSize = gameData.board_size;
  const centerRow = Math.floor(boardSize / 2);
  const centerCol = Math.floor(boardSize / 2);
  
  let centerMoves = 0;
  for (const move of gameData.moves) {
    const distance = Math.abs(move.row - centerRow) + Math.abs(move.col - centerCol);
    if (distance <= 1) centerMoves++;
  }
  
  return centerMoves / gameData.moves.length;
};

/**
 * Calculate pattern completion percentage
 */
const calculatePatternCompletion = (gameData) => {
  // Estimate how close the game was to completion
  const totalCells = gameData.board_size * gameData.board_size;
  const filledCells = gameData.moves.length;
  
  return (filledCells / totalCells) * 100;
};

/**
 * Send game data to backend
 */
const sendGameData = async (gameData) => {
  const base = (typeof window !== 'undefined' && window.location && window.location.port === '3000')
    ? 'http://localhost:8000/'
    : '/';
  
  const response = await fetch(base + 'game-logger.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(gameData)
  });
  
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }
  
  return await response.json();
};

/**
 * Generate UUID
 */
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Get current game data (for debugging)
 */
export const getCurrentGameData = () => {
  return currentGameData;
};

/**
 * Clear current game data
 */
export const clearGameData = () => {
  currentGameData = null;
  gameStartTime = null;
  moveStartTimes = {};
};
